// The table's sounds, made on the spot with Web Audio: a chip's clack, the bet going down, the
// winner's bell and the bust. Nothing is downloaded; nothing plays unless the player wants sound.

import { prefs } from "../state/prefs";

let context: AudioContext | null = null;

function audio(): AudioContext | null {
  if (!prefs.get().sound) return null;
  try {
    context ??= new AudioContext();
    if (context.state === "suspended") void context.resume();
    return context;
  } catch {
    return null;
  }
}

/** A short burst of filtered noise: the click of clay on clay. */
function clack(ac: AudioContext, at: number, pitch: number, level: number) {
  const length = Math.floor(ac.sampleRate * 0.03);
  const buffer = ac.createBuffer(1, length, ac.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i += 1) data[i] = (Math.random() * 2 - 1) * (1 - i / length) ** 3;
  const source = ac.createBufferSource();
  source.buffer = buffer;
  const band = ac.createBiquadFilter();
  band.type = "bandpass";
  band.frequency.value = pitch;
  band.Q.value = 2.5;
  const gain = ac.createGain();
  gain.gain.value = level;
  source.connect(band).connect(gain).connect(ac.destination);
  source.start(at);
}

function tone(ac: AudioContext, at: number, frequency: number, duration: number, level: number, type: OscillatorType = "sine") {
  const osc = ac.createOscillator();
  osc.type = type;
  osc.frequency.value = frequency;
  const gain = ac.createGain();
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(level, at + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
  osc.connect(gain).connect(ac.destination);
  osc.start(at);
  osc.stop(at + duration + 0.05);
}

export const sound = {
  chip() {
    const ac = audio();
    if (!ac) return;
    const now = ac.currentTime;
    clack(ac, now, 3200, 0.5);
    clack(ac, now + 0.035, 2600, 0.3);
  },
  placed() {
    const ac = audio();
    if (!ac) return;
    const now = ac.currentTime;
    for (let i = 0; i < 4; i += 1) clack(ac, now + i * 0.04, 2400 + i * 300, 0.4);
    tone(ac, now + 0.05, 180, 0.25, 0.25);
  },
  win() {
    const ac = audio();
    if (!ac) return;
    const now = ac.currentTime;
    [784, 988, 1175, 1568].forEach((frequency, i) => tone(ac, now + i * 0.09, frequency, 0.9, 0.18));
    tone(ac, now + 0.36, 2093, 1.4, 0.08, "triangle");
  },
  bust() {
    const ac = audio();
    if (!ac) return;
    const now = ac.currentTime;
    tone(ac, now, 140, 0.5, 0.35);
    tone(ac, now + 0.08, 98, 0.7, 0.3);
  },
};
